<template>
  <div 
    v-if="showInstallBanner" 
    class="pwa-install-bar"
    :class="{ 'pwa-install-bar--visible': isVisible }"
  >
    <div class="pwa-install-bar__content">
      <div class="pwa-install-bar__info">
        <svg class="pwa-install-bar__icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2L2 7v10c0 5.55 3.84 9.74 9 11 5.16-1.26 9-5.45 9-11V7l-10-5z"/>
        </svg>
        <div class="pwa-install-bar__text">
          <span class="pwa-install-bar__title">Instalar App</span>
          <span class="pwa-install-bar__subtitle">Instale este app em seu dispositivo para acesso rápido</span>
        </div>
      </div>
      
      <div class="pwa-install-bar__actions">
        <button 
          @click="installPWA" 
          class="pwa-install-bar__button pwa-install-bar__button--install"
          :disabled="isInstalling"
        >
          {{ isInstalling ? 'Instalando...' : 'Instalar' }}
        </button>
        
        <button 
          @click="dismissBanner" 
          class="pwa-install-bar__button pwa-install-bar__button--dismiss"
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PWAInstallBar',
  
  data() {
    return {
      // Controle de visibilidade do banner
      showInstallBanner: true,
      isVisible: false,
      
      // Estados da instalação
      isInstalling: false,
      deferredPrompt: null,
      
      // Configurações de comportamento
      dismissedStorageKey: 'pwa-install-dismissed',
      autoShowDelay: 1000, // 1 segundo para aparecer suavemente
    }
  },
  
  mounted() {
    this.initializePWADetection()
  },
  
  beforeDestroy() {
    this.removeEventListeners()
  },
  
  methods: {
    /**
     * Inicializa toda a detecção de PWA e eventos necessários
     */
    initializePWADetection() {
      // Verifica se já foi dispensado pelo usuário
      if (this.wasPreviouslyDismissed()) {
        return
      }
      
      // Adiciona listeners para eventos de PWA
      this.addEventListeners()
      
      // Verifica se já está instalado
      if (this.isPWAInstalled()) {
        return
      }
      
      // Verifica se o browser suporta PWA
      if (this.browserSupportsPWA()) {
        this.checkForInstallPrompt()
      }
    },
    
    /**
     * Adiciona todos os event listeners necessários
     */
    addEventListeners() {
      // Listener principal para capturar o prompt de instalação
      window.addEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt)
      
      // Listener para quando o app é instalado
      window.addEventListener('appinstalled', this.handleAppInstalled)
      
      // Listener para mudanças no estado standalone (iOS)
      window.addEventListener('DOMContentLoaded', this.checkStandaloneMode)
    },
    
    /**
     * Remove todos os event listeners
     */
    removeEventListeners() {
      window.removeEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', this.handleAppInstalled)
    },
    
    /**
     * Manipula o evento beforeinstallprompt
     */
    handleBeforeInstallPrompt(event) {
      // Previne o prompt automático
      event.preventDefault()
      
      // Armazena o evento para uso posterior
      this.deferredPrompt = event
      
      // Mostra o banner customizado
      this.showBanner()
    },
    
    /**
     * Manipula o evento de app instalado
     */
    handleAppInstalled() {
      this.hideBanner()
      this.deferredPrompt = null
      
      // Emite evento personalizado para o componente pai
      this.$emit('pwa-installed')
    },
    
    /**
     * Verifica se o browser suporta PWA
     */
    browserSupportsPWA() {
      return 'serviceWorker' in navigator && 'PushManager' in window
    },
    
    /**
     * Verifica se o PWA já está instalado
     */
    isPWAInstalled() {
      // Verifica modo standalone (instalado)
      if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
        return true
      }
      
      // Verifica navigator.standalone (iOS)
      if (window.navigator.standalone === true) {
        return true
      }
      
      // Verifica se veio de um app instalado
      if (document.referrer.includes('android-app://')) {
        return true
      }
      
      return false
    },
    
    /**
     * Verifica o modo standalone para iOS
     */
    checkStandaloneMode() {
      if (this.isPWAInstalled()) {
        return
      }
      
      // Para iOS Safari, verifica condições específicas
      if (this.isIOSSafari() && !this.wasPreviouslyDismissed()) {
        setTimeout(() => {
          this.showBanner()
        }, this.autoShowDelay)
      }
    },
    
    /**
     * Detecta se é iOS Safari
     */
    isIOSSafari() {
      const userAgent = window.navigator.userAgent
      const isIOS = /iPad|iPhone|iPod/.test(userAgent)
      const isSafari = /Safari/.test(userAgent) && !/CriOS|FxiOS/.test(userAgent)
      return isIOS && isSafari
    },
    
    /**
     * Verifica se há um prompt de instalação disponível
     */
    checkForInstallPrompt() {
      // Se já temos o deferredPrompt, mostra o banner
      if (this.deferredPrompt) {
        this.showBanner()
        return
      }
      
      // Para outros casos, aguarda um tempo e verifica novamente
      setTimeout(() => {
        if (!this.showInstallBanner && !this.isPWAInstalled()) {
          // Mostra banner genérico para iOS ou outros casos
          if (this.isIOSSafari()) {
            this.showBanner()
          }
        }
      }, this.autoShowDelay)
    },
    
    /**
     * Mostra o banner de instalação
     */
    showBanner() {
      this.showInstallBanner = true
      
      // Pequeno delay para animação suave
      this.$nextTick(() => {
        setTimeout(() => {
          this.isVisible = true
        }, 50)
      })
      
      // Emite evento para o componente pai
      this.$emit('pwa-banner-shown')
    },
    
    /**
     * Esconde o banner de instalação
     */
    hideBanner() {
      this.isVisible = false
      
      setTimeout(() => {
        this.showInstallBanner = false
      }, 300) // Tempo da animação CSS
    },
    
    /**
     * Função principal para instalar o PWA
     */
    async installPWA() {
      if (this.isInstalling) return
      
      this.isInstalling = true
      
      try {
        if (this.deferredPrompt) {
          // Instalação padrão para navegadores que suportam
          await this.performStandardInstall()
        } else if (this.isIOSSafari()) {
          // Instruções para iOS Safari
          this.showIOSInstructions()
        } else {
          // Fallback para outros navegadores
          this.showGenericInstructions()
        }
      } catch (error) {
        console.error('Erro durante instalação do PWA:', error)
        this.$emit('pwa-install-error', error)
      } finally {
        this.isInstalling = false
      }
    },
    
    /**
     * Executa instalação padrão usando o prompt nativo
     */
    async performStandardInstall() {
      // Mostra o prompt nativo
      this.deferredPrompt.prompt()
      
      // Aguarda a resposta do usuário
      const choiceResult = await this.deferredPrompt.userChoice
      
      if (choiceResult.outcome === 'accepted') {
        this.$emit('pwa-install-accepted')
      } else {
        this.$emit('pwa-install-declined')
      }
      
      // Limpa o prompt
      this.deferredPrompt = null
      this.hideBanner()
    },
    
    /**
     * Mostra instruções específicas para iOS
     */
    showIOSInstructions() {
      const message = 'Para instalar este app no iOS:\n\n' +
                     '1. Toque no ícone de compartilhar (□↗)\n' +
                     '2. Role para baixo e toque em "Adicionar à Tela de Início"\n' +
                     '3. Toque em "Adicionar" no canto superior direito'
      
      alert(message)
      this.$emit('pwa-install-instructions-shown', 'ios')
    },
    
    /**
     * Mostra instruções genéricas
     */
    showGenericInstructions() {
      const message = 'Para instalar este app:\n\n' +
                     'Procure pela opção "Instalar app" ou "Adicionar à tela inicial" ' +
                     'no menu do seu navegador (geralmente nos três pontos ⋮)'
      
      alert(message)
      this.$emit('pwa-install-instructions-shown', 'generic')
    },
    
    /**
     * Dispensa o banner e salva a preferência
     */
    dismissBanner() {
      this.hideBanner()
      this.saveDismissedState()
      this.$emit('pwa-banner-dismissed')
    },
    
    /**
     * Verifica se foi dispensado anteriormente
     */
    wasPreviouslyDismissed() {
      try {
        const dismissed = localStorage.getItem(this.dismissedStorageKey)
        const dismissedDate = dismissed ? new Date(dismissed) : null
        
        if (!dismissedDate) return false
        
        // Considera dispensado por 7 dias
        const weekAgo = new Date()
        weekAgo.setDate(weekAgo.getDate() - 7)
        
        return dismissedDate > weekAgo
      } catch (error) {
        // Se localStorage não estiver disponível, não considera dispensado
        return false
      }
    },
    
    /**
     * Salva o estado de dispensado
     */
    saveDismissedState() {
      try {
        localStorage.setItem(this.dismissedStorageKey, new Date().toISOString())
      } catch (error) {
        // Falha silenciosa se localStorage não estiver disponível
        console.warn('Não foi possível salvar estado de PWA dispensado:', error)
      }
    },
    
    /**
     * Método público para forçar exibição do banner
     */
    forceShow() {
      this.showBanner()
    },
    
    /**
     * Método público para esconder o banner
     */
    forceHide() {
      this.hideBanner()
    },
    
    /**
     * Método público para resetar o estado de dispensado
     */
    resetDismissedState() {
      try {
        localStorage.removeItem(this.dismissedStorageKey)
      } catch (error) {
        console.warn('Não foi possível resetar estado de PWA dispensado:', error)
      }
    }
  }
}
</script>

<style scoped>
.pwa-install-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transform: translateY(-100%);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.pwa-install-bar--visible {
  transform: translateY(0);
}

.pwa-install-bar__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  max-width: 1200px;
  margin: 0 auto;
}

.pwa-install-bar__info {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.pwa-install-bar__icon {
  width: 24px;
  height: 24px;
  margin-right: 12px;
  flex-shrink: 0;
  opacity: 0.9;
}

.pwa-install-bar__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.pwa-install-bar__title {
  font-weight: 600;
  font-size: 14px;
  line-height: 1.2;
  margin-bottom: 2px;
}

.pwa-install-bar__subtitle {
  font-size: 12px;
  opacity: 0.9;
  line-height: 1.3;
  display: none;
}

.pwa-install-bar__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.pwa-install-bar__button {
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
}

.pwa-install-bar__button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.pwa-install-bar__button--install {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 8px 16px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.pwa-install-bar__button--install:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-1px);
}

.pwa-install-bar__button--install:active {
  transform: translateY(0);
}

.pwa-install-bar__button--dismiss {
  background: transparent;
  color: white;
  padding: 8px;
  width: 32px;
  height: 32px;
  opacity: 0.8;
}

.pwa-install-bar__button--dismiss:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.1);
}

.pwa-install-bar__button--dismiss svg {
  width: 16px;
  height: 16px;
}

/* Responsividade para telas maiores */
@media (min-width: 480px) {
  .pwa-install-bar__content {
    padding: 14px 20px;
  }
  
  .pwa-install-bar__subtitle {
    display: block;
  }
  
  .pwa-install-bar__title {
    font-size: 15px;
  }
  
  .pwa-install-bar__subtitle {
    font-size: 13px;
  }
}

/* Responsividade para tablets e desktops */
@media (min-width: 768px) {
  .pwa-install-bar__content {
    padding: 16px 24px;
  }
  
  .pwa-install-bar__icon {
    width: 28px;
    height: 28px;
    margin-right: 16px;
  }
  
  .pwa-install-bar__title {
    font-size: 16px;
  }
  
  .pwa-install-bar__subtitle {
    font-size: 14px;
  }
  
  .pwa-install-bar__button--install {
    padding: 10px 20px;
    font-size: 15px;
  }
}

/* Animação de entrada suave */
@keyframes slideDown {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Estados de foco para acessibilidade */
.pwa-install-bar__button:focus {
  outline: 2px solid rgba(255, 255, 255, 0.5);
  outline-offset: 2px;
}

/* Ajustes para modo escuro do sistema */
@media (prefers-color-scheme: dark) {
  .pwa-install-bar {
    background: linear-gradient(135deg, #2d3748 0%, #4a5568 100%);
  }
}

/* Suporte para reduced motion */
@media (prefers-reduced-motion: reduce) {
  .pwa-install-bar {
    transition: none;
  }
  
  .pwa-install-bar__button {
    transition: none;
  }
}
</style>